/**
 * FR-BT3: Boundary Test Generation
 *
 * Generates idempotent Vitest test stubs for every output state of a
 * BoundaryInterface. Unhandled variants get a comment flagging the gap.
 *
 * Output path: .paqad/compliance/boundary-tests/<producer>__<consumer>__<TypeName>.test.ts
 */

import { mkdir, readFile, writeFile } from 'node:fs/promises';
import path from 'node:path';

import type { BoundaryInterface, UnhandledVariant } from './types.js';

export interface GenerateBoundaryTestsOptions {
  project_root: string;
  boundary: BoundaryInterface;
  unhandled: UnhandledVariant[];
  /** Override output directory (default: .paqad/compliance/boundary-tests). */
  output_dir?: string;
}

export interface GeneratedBoundaryTest {
  file_path: string;
  written: boolean; // false = already existed and was identical (idempotent)
}

export async function generateBoundaryTests(
  options: GenerateBoundaryTestsOptions,
): Promise<GeneratedBoundaryTest[]> {
  const outputDir = path.resolve(
    options.project_root,
    options.output_dir ?? '.paqad/compliance/boundary-tests',
  );
  await mkdir(outputDir, { recursive: true });

  const results: GeneratedBoundaryTest[] = [];

  for (const consumer of options.boundary.consumer_specs) {
    const fileName = buildFileName(options.boundary, consumer);
    const filePath = path.join(outputDir, fileName);
    const content = renderTestFile(options.boundary, consumer, options.unhandled);

    const written = await writeIfChanged(filePath, content);
    results.push({ file_path: filePath, written });
  }

  return results;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function buildFileName(boundary: BoundaryInterface, consumerSpec: string): string {
  const producer = boundary.producer_spec ?? 'unknown';
  const slugify = (s: string) => s.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
  return `${slugify(producer)}__${slugify(consumerSpec)}__${boundary.type_name}.test.ts`;
}

function renderTestFile(
  boundary: BoundaryInterface,
  consumerSpec: string,
  allUnhandled: UnhandledVariant[],
): string {
  const unhandledStates = new Set(
    allUnhandled.filter((u) => u.consumer_spec === consumerSpec).map((u) => u.state),
  );

  const testBlocks = boundary.output_states.map((state) => {
    const isUnhandled = unhandledStates.has(state);
    const unhandledComment = isUnhandled
      ? `  // NOTE: this state is produced by ${boundary.producer_spec ?? 'unknown'} but not explicitly handled by ${consumerSpec}.\n`
      : '';
    return [
      `  it('handles ${boundary.type_name} state: ${state}', () => {`,
      unhandledComment,
      `    // TODO: construct a ${boundary.type_name} fixture in state "${state}" and pass it`,
      `    // to the consumer entry point. Assert no crash or unhandled-case error.`,
      `    expect(true).toBe(true); // replace with real assertion`,
      `  });`,
    ]
      .filter(Boolean)
      .join('\n');
  });

  return [
    `/**`,
    ` * Boundary test: ${boundary.type_name}`,
    ` * Producer: ${boundary.producer_spec ?? 'unknown'}`,
    ` * Consumer: ${consumerSpec}`,
    ` *`,
    ` * Auto-generated by compliance boundary. Do not edit header.`,
    ` * Add assertions to each stub; header will be preserved on regeneration.`,
    ` */`,
    `import { describe, expect, it } from 'vitest';`,
    ``,
    `describe('boundary: ${boundary.type_name} → ${consumerSpec}', () => {`,
    ...testBlocks,
    `});`,
    ``,
  ].join('\n');
}

async function writeIfChanged(filePath: string, content: string): Promise<boolean> {
  try {
    const existing = await readFile(filePath, 'utf8');
    if (existing === content) return false;
  } catch {
    // File does not exist — write it
  }
  await writeFile(filePath, content, 'utf8');
  return true;
}
